import { zodResolver } from "@hookform/resolvers/zod";
import { Buttonv2 } from "@itandi/itandi-bb-ui/Buttonv2";
import { Divider } from "@itandi/itandi-bb-ui/Divider";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { InputCheckBox } from "@itandi/itandi-bb-ui/InputCheckBox";
import { InputSelect } from "@itandi/itandi-bb-ui/InputSelect";
import { InputText } from "@itandi/itandi-bb-ui/InputText";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import type { ChangeEvent } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useSWRConfig } from "swr";
import useSWRMutation from "swr/mutation";
import type {
	CompanyWorkflowSetting,
	WorkflowResourceType
} from "~/models/CompanyWorkflowSetting";
import { isApprovalRequiredWorkflow } from "~/models/CompanyWorkflowSetting";
import type { WorkflowStartFormValues } from "~/models/CompanyWorkflowStart";
import {
	buildDefaultWorkflowStartFormValues,
	buildResetWorkflowStartFormValues,
	workflowStartSchema
} from "~/models/CompanyWorkflowStart";
import { isSuccess } from "~/utils/api/api_handler";
import { create as startDealWorkflow } from "~/utils/api/deals/DealWorkflow";
import { generatePath } from "~/utils/generatePath";
import { itandiTaskWorkflowUrl } from "~/utils/ItandiTaskPath";

type Props = {
	companyWorkflowSettings: CompanyWorkflowSetting[];
	dealId: number;
	handleClose: () => void;
	resourceType: WorkflowResourceType;
};

export function WorkflowStartModal({
	companyWorkflowSettings,
	dealId,
	handleClose,
	resourceType
}: Props) {
	const { notify } = useNotification();
	const { mutate: globalMutate } = useSWRConfig();
	const {
		control,
		getValues,
		register,
		handleSubmit,
		reset,
		formState: { errors }
	} = useForm<WorkflowStartFormValues>({
		defaultValues: buildDefaultWorkflowStartFormValues(),
		resolver: zodResolver(workflowStartSchema)
	});
	const workflowUuid = useWatch({ control, name: "workflowUuid" });

	const workflowDefinitions =
		companyWorkflowSettings.find(
			(setting) => setting.resourceType === resourceType
		)?.workflowDefinitions ?? [];

	const handleChangeWorkflowUuid = (
		event: ChangeEvent<HTMLSelectElement>
	): void => {
		reset(
			buildResetWorkflowStartFormValues(getValues(), event.currentTarget.value)
		);
	};

	const { trigger, isMutating } = useSWRMutation(
		{
			dealId,
			url: generatePath("/deals/:deal_id/deal_workflows", { deal_id: dealId })
		},
		async (
			_key,
			{ arg }: { arg: WorkflowStartFormValues }
		): ReturnType<typeof startDealWorkflow> =>
			startDealWorkflow(dealId, {
				afterTheFactApproval: arg.proceedWithoutApproval,
				comment: arg.comment,
				resourceType,
				workflowDefinitionUuid: arg.workflowUuid
			})
	);

	const onSubmit = handleSubmit(async (values) => {
		const result = await trigger(values);
		if (isSuccess(result)) {
			notify({ content: "ワークフローを開始しました。", variant: "success" });
			window.open(
				itandiTaskWorkflowUrl(result.data!.workflowId),
				"_blank",
				"noopener,noreferrer"
			);
			await globalMutate(
				generatePath("/deals/:deal_id/deal_workflow_restriction", {
					deal_id: dealId
				})
			);
			handleClose();
		} else {
			notify({
				content: result.message ?? "ワークフローの開始に失敗しました。",
				variant: "error"
			});
		}
	});

	return (
		<Modalv2 isOpen onClose={handleClose}>
			<ModalHeader title="ワークフローの開始" />
			<form noValidate onSubmit={onSubmit}>
				<ModalBody>
					<FormGroup variant="vertical">
						<FormControl
							chip="必須"
							isInvalid={!!errors.workflowUuid}
							label="ワークフローの選択"
						>
							<InputSelect
								isInvalid={!!errors.workflowUuid}
								{...register("workflowUuid")}
								onChange={handleChangeWorkflowUuid}
							>
								<option value="">選択してください</option>
								{workflowDefinitions.map((definition) => (
									<option key={definition.id} value={definition.workflowUuid}>
										{definition.name}
									</option>
								))}
							</InputSelect>
							<FormControlText error={errors.workflowUuid?.message} />
						</FormControl>
						<Divider />
						{isApprovalRequiredWorkflow(workflowDefinitions, workflowUuid) ? (
							<>
								<FormControl label="事後承認">
									<Controller
										control={control}
										name="proceedWithoutApproval"
										render={({ field }) => (
											<InputCheckBox
												checked={field.value}
												label="承認を待たずに先行発注し、事後承認扱いとする"
												onChange={(e) => {
													field.onChange(e.currentTarget.checked);
												}}
											/>
										)}
									/>
								</FormControl>
								<Divider />
							</>
						) : null}
						<FormControl isInvalid={!!errors.comment} label="コメント">
							<InputText
								isInvalid={!!errors.comment}
								placeholder="例: 内容についてご確認をお願いいたします"
								resize="autoRows"
								rows={4}
								{...register("comment")}
							/>
							<FormControlText error={errors.comment?.message} />
						</FormControl>
					</FormGroup>
				</ModalBody>
				<ModalFooter
					leftArea={
						<Buttonv2
							disabled={isMutating}
							onClick={handleClose}
							variant="secondary"
						>
							キャンセル
						</Buttonv2>
					}
					rightArea={
						<Buttonv2 loading={isMutating} type="submit" variant="primary">
							開始
						</Buttonv2>
					}
				/>
			</form>
		</Modalv2>
	);
}
